// API hooks for the Agents feature. Mostly plain useState/useEffect + fpFetch,
// to match the pattern used by ReportList and MatrixPage. State that several
// surfaces share (the agent files lock) lives in React Query instead: a shared
// cache needs an owner, and a module-level Map has none (#12187).

import type { ThinkingEffort } from "../../Chat/thinkingEffort";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  downloadApiFile,
  fetchApiFile,
} from "../../../../services/downloadApiFile";
import fpFetch from "../../../../services/fpFetch";
import { preflightStaffIngress } from "@/services/staffIngress";
import type { ChatCitationSource, ReferencedEntity } from "../../Chat/types";
import type {
  AttachmentRequestFields,
  FolderPickFields,
} from "../../Chat/composerSelection";
import {
  errorFromResponse,
  NETWORK_ERROR_STATUS,
  statusOf,
  userMessageOf,
} from "../../../../services/apiError";
import { BASE_API_URL_V2 } from "../../../../constants";
import { foldSessionSnapshot, insertSessionEvent } from "../sessionEvents";
import {
  setProductTimeout,
  clearProductTimeout,
  DeadlineExceededError,
  retryWithBackoff,
  withDeadline,
} from "@/utils/productTimers";
import type {
  AgentCreditUsage,
  AgentDetail,
  AgentSummary,
  ChatEventDto,
  MemoryFile,
  MemoryLockStatus,
  SessionDetail,
  SessionStatus,
} from "../types";

const apiPath = (path: string) => `${BASE_API_URL_V2}/api/v2${path}`;

async function jsonFetch<T>(
  url: string,
  init: RequestInit & { "x-finpilot-auth"?: boolean } = {}
): Promise<T> {
  const response = await fpFetch(url, {
    "x-finpilot-auth": true,
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!response.ok) {
    // Shared so a network-level failure reads as a sentence rather than
    // `undefined: undefined` (#11634). The thrown error carries the status, so
    // callers can branch on it (e.g. the chat renders a 402 weekly-credit block
    // specially) without parsing the message.
    throw await errorFromResponse(response);
  }
  return (await response.json()) as T;
}

export function useAgentList() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await jsonFetch<{ items: AgentSummary[] }>(
        apiPath("/agents")
      );
      setAgents(data.items);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** One agent's fields replaced in place, without re-reading the list (an
   * icon or colour picked from its tile). */
  const patch = useCallback(
    (agentId: string, next: Partial<AgentSummary>) =>
      setAgents((prev) =>
        prev.map((a) => (a.id === agentId ? { ...a, ...next } : a))
      ),
    []
  );

  return { agents, loading, error, refresh, patch };
}

/** One answer's citation entries with their passages, by marker (#12081):
 * what a chip whose entry the history read left without a passage opens on. */
export async function fetchAnswerCitations(
  agentId: string,
  sessionId: string,
  eventId: string,
  signal: AbortSignal
): Promise<Record<string, ChatCitationSource>> {
  const data = await jsonFetch<{ markers: Record<string, ChatCitationSource> }>(
    apiPath(
      `/agents/${agentId}/sessions/${sessionId}/events/${eventId}/citations`
    ),
    { signal }
  );
  return data.markers;
}

export async function createAgent(input: {
  name: string;
  description?: string;
  icon?: string;
  color?: string;
  system_prompt: string;
  internal_readme?: string;
}): Promise<AgentDetail> {
  return jsonFetch<AgentDetail>(apiPath("/agents"), {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export function useAgentDetail(agentId: string | undefined) {
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // An agent already on the page: a re-read (a saved edit) replaces it in
  // place rather than showing the loading screen, which unmounted every
  // tab and threw a draft away mid-edit (round 20 review).
  const hasAgentRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!agentId) {
      setAgent(null);
      setLoading(false);
      return;
    }
    if (!hasAgentRef.current) setLoading(true);
    setError(null);
    try {
      const data = await jsonFetch<AgentDetail>(apiPath(`/agents/${agentId}`));
      setAgent(data);
      hasAgentRef.current = true;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    hasAgentRef.current = false;
    void refresh();
  }, [refresh]);

  // A field saved in place (a rename, a description, a colour): the agent
  // is replaced without a reload, so the page never shows its loading
  // state for a change it made itself.
  const patch = useCallback((next: Partial<AgentDetail>) => {
    setAgent((prev) => (prev ? { ...prev, ...next } : prev));
  }, []);

  return { agent, loading, error, refresh, patch };
}

export async function updateAgentMetadata(
  agentId: string,
  input: {
    name?: string;
    description?: string | null;
    icon?: string | null;
    color?: string | null;
  }
): Promise<AgentDetail> {
  return jsonFetch<AgentDetail>(apiPath(`/agents/${agentId}`), {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function archiveAgent(agentId: string): Promise<AgentDetail> {
  return jsonFetch<AgentDetail>(apiPath(`/agents/${agentId}`), {
    method: "DELETE",
  });
}

/** Duplicate an agent's definition + memory files into a brand-new, private
 * agent owned by the caller. Anyone with access (including view-only) can
 * copy. Returns the new agent's detail so the caller can navigate to it. */
export async function copyAgent(
  agentId: string,
  includeRuns = false
): Promise<AgentDetail> {
  return jsonFetch<AgentDetail>(apiPath(`/agents/${agentId}/copy`), {
    method: "POST",
    body: JSON.stringify({ include_runs: includeRuns }),
  });
}

/** Thin mutation hook around ``copyAgent`` that tracks in-flight + error
 * state, matching the plain useState idiom the rest of this file uses (no
 * React Query). The caller is responsible for refreshing the agent list and
 * navigating to the returned copy. */
export function useCopyAgent() {
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const copy = useCallback(
    async (agentId: string, includeRuns = false): Promise<AgentDetail> => {
      setCopying(true);
      setError(null);
      try {
        return await copyAgent(agentId, includeRuns);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        throw err;
      } finally {
        setCopying(false);
      }
    },
    []
  );

  const resetError = useCallback(() => setError(null), []);
  return { copy, copying, error, resetError };
}

export async function setAgentFolder(
  agentId: string,
  folder: string | null
): Promise<AgentDetail> {
  return jsonFetch<AgentDetail>(apiPath(`/agents/${agentId}/folder`), {
    method: "POST",
    body: JSON.stringify({ folder }),
  });
}

/** Renames a folder on every agent filed in it: the agents list's in-place
 * folder rename (#12414). */
export async function renameAgentFolder(
  oldFolder: string,
  newFolder: string
): Promise<void> {
  await jsonFetch<{ ok: boolean }>(apiPath(`/agents/folders/rename`), {
    method: "POST",
    body: JSON.stringify({ old_folder: oldFolder, new_folder: newFolder }),
  });
}

/** A Run groups a shared input/output filesystem with the conversations
 * (browser-style tabs) that operate over it. Mirrors the backend
 * RunSummary schema in fp/routes/agents.py. */
export interface RunSummary {
  id: string;
  title?: string;
  selected_document_ids: string[];
  reference_document_ids: string[];
  created_at: string;
  last_activity_at: string;
  conversation_count: number;
  // Display name of the user who started the run, so a shared agent's runs are
  // attributable in the list (#10155). Null when not resolved.
  created_by_name?: string | null;
  // Did the viewer start this run? Drives the Runs tab's ownership filter
  // (#11507). Server-computed rather than derived from created_by_name, which
  // is masked to "Finpilot" for staff accounts (#10570) and so cannot identify
  // anyone.
  created_by_me: boolean;
  // Whether an automation started the run (#11942): the Runs tab marks it
  // with the lightning glyph. Absent on older payloads.
  automated?: boolean;
  // Where the run's workflow stands (#11942 round 35): the state for the
  // chip's tone, the label as the server says it to this viewer ("Waiting
  // on you"). Null or absent on a run without a workflow list.
  workflow?: RunWorkflowStanding | null;
  // The skill this run sets up: an agent task's own setup conversation
  // (#12255). Null or absent for every other run.
  setup_skill?: string | null;
}

export type RunWorkflowState =
  | "done"
  | "running"
  | "waiting"
  | "paused"
  | "failed"
  | "in_progress";

export interface RunWorkflowStanding {
  state: RunWorkflowState;
  label: string;
}

export interface CopyRunResponse {
  agent: AgentDetail;
  run: RunSummary;
}

export async function copyRun(
  agentId: string,
  runId: string
): Promise<CopyRunResponse> {
  return jsonFetch<CopyRunResponse>(
    apiPath(`/agents/${agentId}/runs/${runId}/copy`),
    { method: "POST", body: JSON.stringify({}) }
  );
}

/** One conversation tab inside a Run. The `id` is interchangeable with
 * the legacy session id — the chat surface binds to it unchanged. */
export interface RunConversation {
  id: string;
  title?: string;
  status: SessionStatus;
  started_at?: string;
  last_activity_at: string;
  created_at: string;
}

interface RunCreateResponse {
  run: RunSummary;
  conversation: RunConversation;
}

export interface RunDetailResponse {
  run: RunSummary;
  conversations: RunConversation[];
}

/** The agent's runs, read once (#11942 round 10: the Steps tab looks for
 * the agent's "Change the steps" run among them). */
export async function listAgentRuns(agentId: string): Promise<RunSummary[]> {
  const data = await jsonFetch<{ items: RunSummary[] }>(
    apiPath(`/agents/${agentId}/runs`)
  );
  return data.items;
}

/** One run with its conversations, read once (#11942 round 10: the Steps
 * tab finds the agent's "Change the steps" conversation this way). */
export async function fetchRunDetail(
  agentId: string,
  runId: string
): Promise<RunDetailResponse> {
  return jsonFetch<RunDetailResponse>(
    apiPath(`/agents/${agentId}/runs/${runId}`)
  );
}

export function useAgentRuns(agentId: string | undefined) {
  const [runs, setRuns] = useState<RunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!agentId) {
      setRuns([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await jsonFetch<{ items: RunSummary[] }>(
        apiPath(`/agents/${agentId}/runs`)
      );
      setRuns(data.items);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { runs, loading, error, refresh };
}

export async function createRun(
  agentId: string,
  input: {
    title?: string;
    selectedDocumentIds: string[];
    referenceDocumentIds?: string[];
    /** The skill the run sets up: an agent task's setup (#12255). */
    setupSkill?: string;
    /** The folders the documents were picked through, recorded on the run
     * (#12495). */
    folderPicks?: FolderPickFields;
  }
): Promise<RunCreateResponse> {
  const body: {
    title?: string;
    selected_document_ids: string[];
    reference_document_ids?: string[];
    setup_skill?: string;
  } & FolderPickFields = {
    selected_document_ids: input.selectedDocumentIds,
    ...input.folderPicks,
  };
  if (input.title !== undefined) body.title = input.title;
  if (input.referenceDocumentIds !== undefined) {
    body.reference_document_ids = input.referenceDocumentIds;
  }
  if (input.setupSkill !== undefined) body.setup_skill = input.setupSkill;
  return jsonFetch<RunCreateResponse>(apiPath(`/agents/${agentId}/runs`), {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** What a caller that just created the run already holds, so the run view can
 * mount from it instead of behind its loading state (#11987 round 2). */
export interface RunDetailSeed {
  run: RunSummary;
  conversations: RunConversation[];
}

export function useRunDetail(
  agentId: string | undefined,
  runId: string | undefined,
  seed: RunDetailSeed | null = null
) {
  const [run, setRun] = useState<RunSummary | null>(seed?.run ?? null);
  const [conversations, setConversations] = useState<RunConversation[]>(
    seed?.conversations ?? []
  );
  const [loading, setLoading] = useState(seed === null);
  const [error, setError] = useState<string | null>(null);
  // Which run is on screen. `loading` flips only while THIS run is not held:
  // the run view swaps itself for its loading state on `loading`, so a
  // refresh of a run already on screen (the bar's Refresh, a mutation's
  // refetch) has to be silent, and a seeded mount starts held. Switching to
  // another run still loads visibly.
  const heldRef = useRef<string | null>(
    seed ? `${agentId ?? ""}/${runId ?? ""}` : null
  );
  // A pushed name never loses to a read already in flight (frontend rule 6,
  // #12187). The read's snapshot can predate the `run_titled` frame, and
  // `setRun(data.run)` would put its null title back over the name. So the
  // name pushed while any read is out is kept here and laid over each read
  // that lands; it is dropped once no read is out, when the next read is
  // sent after the name existed and carries it. The name is kept with the run
  // it was pushed for, and laid only over a read of that run.
  const readsInFlightRef = useRef(0);
  const titlePushedDuringReadRef = useRef<{
    key: string;
    title: string;
  } | null>(null);
  // The run the hook is showing now. The view is reused when the page moves
  // to another run, so a read of the run it left can land after the next
  // run's own read; it belongs to the run it read and is dropped (#12187).
  // A layout effect, so it moves before the next run's read is sent.
  const currentKeyRef = useRef(`${agentId ?? ""}/${runId ?? ""}`);
  useLayoutEffect(() => {
    currentKeyRef.current = `${agentId ?? ""}/${runId ?? ""}`;
  }, [agentId, runId]);

  const refresh = useCallback(async () => {
    if (!agentId || !runId) {
      setRun(null);
      setConversations([]);
      setLoading(false);
      return;
    }
    const key = `${agentId}/${runId}`;
    if (heldRef.current !== key) setLoading(true);
    setError(null);
    readsInFlightRef.current += 1;
    try {
      const data = await jsonFetch<RunDetailResponse>(
        apiPath(`/agents/${agentId}/runs/${runId}`)
      );
      if (currentKeyRef.current !== key) return;
      const pushed = titlePushedDuringReadRef.current;
      setRun(
        pushed !== null && pushed.key === key
          ? { ...data.run, title: pushed.title }
          : data.run
      );
      setConversations(data.conversations);
      heldRef.current = key;
    } catch (err: unknown) {
      if (currentKeyRef.current !== key) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      readsInFlightRef.current -= 1;
      if (readsInFlightRef.current === 0) {
        titlePushedDuringReadRef.current = null;
      }
      // A read of a run left behind does not end the next run's loading.
      if (currentKeyRef.current === key) setLoading(false);
    }
  }, [agentId, runId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // The run's generated name (#11506) arrives over the open conversation's
  // socket. Applied in place rather than by refetching: `refresh` flips
  // `loading`, which swaps the whole run view — including the conversation
  // whose turn just produced the name — for the loading state.
  const applyRunTitle = useCallback((title: string) => {
    if (readsInFlightRef.current > 0) {
      titlePushedDuringReadRef.current = { key: currentKeyRef.current, title };
    }
    setRun((prev) => (prev ? { ...prev, title } : prev));
  }, []);

  // The open conversation's live status (#11861), patched in place for the
  // same reason as the title above. The run header's chip reads this array,
  // and before this it was a page-load snapshot: a run that had finished went
  // on reading "Running" until the user reloaded. Returning the previous array
  // when nothing moved absorbs the report the conversation makes on mount,
  // which restates the status the run fetch had already delivered; it is worth
  // one line to not mint an array for a value that did not change.
  const applyConversationStatus = useCallback(
    (conversationId: string, status: SessionStatus) => {
      setConversations((prev) => {
        let changed = false;
        const next = prev.map((conversation) => {
          if (conversation.id !== conversationId) return conversation;
          if (conversation.status === status) return conversation;
          changed = true;
          return { ...conversation, status };
        });
        return changed ? next : prev;
      });
    },
    []
  );

  return {
    run,
    conversations,
    loading,
    error,
    refresh,
    applyRunTitle,
    applyConversationStatus,
  };
}

export async function createConversationInRun(
  agentId: string,
  runId: string
): Promise<RunConversation> {
  return jsonFetch<RunConversation>(
    apiPath(`/agents/${agentId}/runs/${runId}/conversations`),
    { method: "POST" }
  );
}

/** How often the agents UI re-reads the org's credit standing while its tab is
 * in the foreground (#11732). The pool is org-wide and moves whenever any member
 * runs a turn, so a figure fixed at page load drifts away both from the server
 * and from what a colleague is looking at. Exported so the cadence itself is
 * pinned by test: too slow stops fixing the staleness, too fast turns every open
 * tab into a load generator against an endpoint that hits Postgres per call. */
export const CREDIT_USAGE_POLL_MS = 60_000;

// The caller org's weekly agent-credit usage, for the agents-page allowance
// ring. Null limit/pct means the org has no weekly limit (unlimited).
export function useAgentCreditUsage() {
  // React Query's own interval already skips a background tab
  // (`refetchIntervalInBackground` defaults to false), which is what the
  // hand-rolled `setInterval` + `document.visibilityState` check used to do.
  const query = useQuery({
    queryKey: ["agents", "credit-usage"],
    queryFn: ({ signal }) =>
      jsonFetch<AgentCreditUsage>(apiPath("/agents/credit-usage"), { signal }),
    refetchInterval: CREDIT_USAGE_POLL_MS,
    // One read per poll, as before: a miss waits for the next poll rather
    // than retrying, and the reveal read below is the only focus refetch
    // (React Query's own would cancel it and read a second time).
    retry: false,
    refetchOnWindowFocus: false,
    // The previously loaded figure is deliberately left in place on error
    // (React Query's default): a number a minute old is more useful than a
    // ring that empties itself because one poll happened to miss.
  });
  const refresh = useCallback(() => {
    void query.refetch();
  }, [query.refetch]);
  useRefreshOnReveal(refresh);

  return {
    usage: query.data ?? null,
    // Only ever true before the first figure lands: a background poll's
    // `isFetching` must not flick a ring that already has a figure back
    // into a loading state.
    loading: query.isLoading,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : String(query.error)
      : null,
    refresh,
  };
}

export async function deleteSession(
  agentId: string,
  sessionId: string
): Promise<void> {
  await jsonFetch<{ deleted: boolean }>(
    apiPath(`/agents/${agentId}/sessions/${sessionId}`),
    { method: "DELETE" }
  );
}

/** Soft-delete a run and every conversation in it. Refuses (409) while any
 * conversation is still active. */
export async function deleteRun(agentId: string, runId: string): Promise<void> {
  await jsonFetch<{ deleted: boolean }>(
    apiPath(`/agents/${agentId}/runs/${runId}`),
    { method: "DELETE" }
  );
}

/** Rename a run (set its display title). Returns the updated summary. */
export async function renameRun(
  agentId: string,
  runId: string,
  title: string
): Promise<RunSummary> {
  return jsonFetch<RunSummary>(apiPath(`/agents/${agentId}/runs/${runId}`), {
    method: "PATCH",
    body: JSON.stringify({ title }),
  });
}

export async function updateSessionDocuments(
  agentId: string,
  sessionId: string,
  input: {
    selected_document_ids?: string[];
    reference_document_ids?: string[];
  } & FolderPickFields
): Promise<SessionFiles> {
  return jsonFetch<SessionFiles>(
    apiPath(`/agents/${agentId}/sessions/${sessionId}/documents`),
    { method: "PUT", body: JSON.stringify(input) }
  );
}

/** How long one read of a conversation's history may take before it is
 * abandoned and tried again (#12081). Generous on purpose: the largest
 * conversations take seconds to read, and a deadline that fires on a slow but
 * healthy read would turn it into a loop that never lands. */
export const SESSION_HISTORY_DEADLINE_MS = 60_000;
/** Reads of the history before the page shows the failure, the first
 * included. */
export const SESSION_HISTORY_ATTEMPTS = 3;
/** Wait before the second read; doubles after each. */
export const SESSION_HISTORY_RETRY_BASE_MS = 1_000;

/** A read worth trying again: it stalled, it never reached the server, or the
 * server failed. A 4xx is an answer, and asking again gets the same one. */
function historyReadRetryable(error: unknown): boolean {
  if (error instanceof DeadlineExceededError) return true;
  const status = statusOf(error);
  if (status === undefined) return false;
  return (
    status === NETWORK_ERROR_STATUS ||
    status === 408 ||
    status === 429 ||
    status >= 500
  );
}

export function useSessionDetail(
  agentId: string | undefined,
  sessionId: string | undefined
) {
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Events pushed before the first load resolved. There is no detail to append
  // them to yet, and dropping them lost the opening of a turn outright
  // (#11464), so they wait here and the load folds them in. Keyed by event id
  // to collapse a repeated push of the same event.
  //
  // Populated ONLY while `detail` is null. Buffering unconditionally re-supplied
  // events the fold is responsible for preserving, which hid a revert of the fix
  // from every test, and it pinned the socket's unfiltered copy of every event
  // in memory for the life of the mount.
  const pendingEventsRef = useRef<Map<string, ChatEventDto>>(new Map());
  // Whether a snapshot has landed for the CURRENT conversation, so appends know
  // whether there is a transcript to insert into. A ref rather than reading
  // `detail`, which `appendEvent` would capture stale.
  const loadedRef = useRef(false);
  // A session change (a turn's terminal status) pushed while a load is in
  // flight (#12187). That load's snapshot was taken before the change, and
  // `refresh` assigns its session wholesale, so it would put the old
  // "running" back over the change, or, before the first load, the change was
  // dropped for want of a session to patch: either way the working state
  // stayed up and the queue never drained. The session carries no turn id, so
  // the push cannot be ordered against the snapshot (a new turn may have
  // started since). The load re-reads once instead; that read was issued
  // after the push, so it is at least as new as both.
  const rereadAfterLoadRef = useRef(false);
  // The pushed change itself, until that re-read lands. The snapshot in
  // flight predates it, so a load landing meanwhile keeps these fields over
  // the snapshot's rather than showing the older state for the length of
  // the re-read (rule 6: a load's older snapshot never overwrites a push). A
  // send answered while the first open's read was out used to take the turn
  // back to idle for that long: the working line went and came back, and a
  // reader at the end of the transcript was left above it (#12187).
  const patchedDuringLoadRef = useRef<Partial<SessionDetail["session"]> | null>(
    null
  );
  const loadsInFlightRef = useRef(0);
  // Bumped on every conversation switch. The page is reused across sessions,
  // so a load for the one it left can land after the switch; it must neither
  // write its session nor take the new conversation's re-read.
  const generationRef = useRef(0);

  // Full reads in flight. Only these show as `loading`: a delta never
  // raises it, and the delta that follows the first read must not hold it
  // either, or the page waits a second round trip to leave its skeleton.
  const fullLoadsInFlightRef = useRef(0);

  // The highest `seq` the last server read covered (#12081). A delta read
  // starts here, not at the highest row the page holds: a socket row can be
  // one the read never returns (a hidden row), and a row the socket dropped
  // below it would never be read back.
  //
  // A delta from a high-water mark misses nothing only because an agent
  // conversation's events are append-only and their `seq` is allocated under
  // the conversation lock until commit, so commit order is `seq` order. If
  // either stops being true, a delta silently skips rows.
  const readHighWaterRef = useRef(-1);
  // The socket opened (or reopened) while the first read was out. That read's
  // snapshot may predate the subscription, so once it lands, read what is
  // newer: the backfill #12187 added, without a second full read.
  const backfillAfterLoadRef = useRef(false);
  // Aborted when the page leaves the conversation: stops every read in
  // flight and every retry still waiting out its backoff.
  const leftRef = useRef<AbortController>(new AbortController());

  /** One read of the history, bounded and retried (#12081). A read with no
   * deadline that never answered left the page on its skeleton for good. */
  const readHistory = useCallback(
    (sinceSeq: number): Promise<SessionDetail> => {
      const left = leftRef.current.signal;
      const query = sinceSeq < 0 ? "" : `?since_seq=${sinceSeq}`;
      const url = apiPath(`/agents/${agentId}/sessions/${sessionId}${query}`);
      return retryWithBackoff(
        async () => {
          // A retry whose backoff ran past the page leaving the conversation.
          left.throwIfAborted();
          const controller = new AbortController();
          const stop = () => controller.abort();
          left.addEventListener("abort", stop);
          try {
            return await withDeadline(
              jsonFetch<SessionDetail>(url, { signal: controller.signal }),
              SESSION_HISTORY_DEADLINE_MS
            );
          } catch (err: unknown) {
            // A read past its deadline is still downloading; stop it.
            controller.abort();
            throw err;
          } finally {
            left.removeEventListener("abort", stop);
          }
        },
        {
          attempts: SESSION_HISTORY_ATTEMPTS,
          baseMs: SESSION_HISTORY_RETRY_BASE_MS,
          shouldRetry: (err) => !left.aborted && historyReadRetryable(err),
        }
      );
    },
    [agentId, sessionId]
  );

  /** Read the history: all of it ("full"), or what is newer than the last
   * read ("delta"). */
  const load = useCallback(
    async (requested: "full" | "delta"): Promise<void> => {
      if (!agentId || !sessionId) {
        setDetail(null);
        setLoading(false);
        return;
      }
      let mode = requested;
      if (mode === "delta" && !loadedRef.current) {
        // No snapshot yet. With the first read out, wait for it (it will
        // read what is newer when it lands); with none out (it failed),
        // only a full read can fill the page.
        if (loadsInFlightRef.current > 0) {
          backfillAfterLoadRef.current = true;
          return;
        }
        mode = "full";
      }
      const generation = generationRef.current;
      const sinceSeq = mode === "delta" ? readHighWaterRef.current : -1;
      const full = mode === "full";
      loadsInFlightRef.current += 1;
      if (full) {
        fullLoadsInFlightRef.current += 1;
        setLoading(true);
        setError(null);
      }
      try {
        const data = await readHistory(sinceSeq);
        if (generation !== generationRef.current) return;
        readHighWaterRef.current = data.events.reduce(
          (max, e) => (e.seq > max ? e.seq : max),
          Math.max(readHighWaterRef.current, sinceSeq)
        );
        // Read and clear the buffer OUTSIDE the updater: the updater has to
        // stay pure, because React may invoke it more than once.
        const buffered = [...pendingEventsRef.current.values()];
        pendingEventsRef.current.clear();
        const patched = patchedDuringLoadRef.current;
        loadedRef.current = true;
        setError(null);
        // Fold, don't overwrite (#11464). This response is a snapshot of the
        // moment the request was issued, so anything the socket delivered
        // while it was in flight is missing from it — assigning it over the
        // top is what deleted answers the user had already read. The
        // functional form is load-bearing: it reads the list as it stands at
        // apply time rather than whatever it was when the request went out.
        setDetail((prev) => ({
          session: patched ? { ...data.session, ...patched } : data.session,
          events: foldSessionSnapshot(
            [...(prev?.events ?? []), ...buffered],
            data.events,
            sinceSeq
          ),
        }));
        // Re-read once the last overlapping load is done, not after each: an
        // earlier load landing does not make a later one's snapshot current.
        // What is newer is all it needs: a delta carries the session block.
        if (
          loadsInFlightRef.current === 1 &&
          (rereadAfterLoadRef.current || backfillAfterLoadRef.current)
        ) {
          rereadAfterLoadRef.current = false;
          backfillAfterLoadRef.current = false;
          // The re-read leaves after the push, so its snapshot carries it.
          patchedDuringLoadRef.current = null;
          void load("delta");
        }
      } catch (err: unknown) {
        if (generation !== generationRef.current) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (generation === generationRef.current) {
          loadsInFlightRef.current -= 1;
          if (full) {
            fullLoadsInFlightRef.current -= 1;
            if (fullLoadsInFlightRef.current === 0) setLoading(false);
          }
        }
      }
    },
    [agentId, sessionId, readHistory]
  );

  /** Read the whole conversation again. */
  const refresh = useCallback((): Promise<void> => load("full"), [load]);

  /** Read what is newer than the last read: the socket's (re)open backfill,
   * for the rows pushed while it was not subscribed. */
  const backfill = useCallback((): Promise<void> => load("delta"), [load]);

  /** Apply a pushed change to the session's own fields (status, totals). */
  const patchSession = useCallback(
    (patch: Partial<SessionDetail["session"]>) => {
      // A load in flight would overwrite this with an older snapshot, so it
      // re-reads when it lands (see rereadAfterLoadRef).
      if (!loadedRef.current || loadsInFlightRef.current > 0) {
        rereadAfterLoadRef.current = true;
        patchedDuringLoadRef.current = {
          ...patchedDuringLoadRef.current,
          ...patch,
        };
      }
      setDetail((prev) =>
        prev
          ? { session: { ...prev.session, ...patch }, events: prev.events }
          : prev
      );
    },
    []
  );

  /** Add one persisted event (websocket push or send-message response) to the
   *  transcript, in server order. */
  const appendEvent = useCallback((event: ChatEventDto) => {
    if (!loadedRef.current) {
      // Nothing to append to yet; the load folds this in.
      pendingEventsRef.current.set(event.event_id, event);
      return;
    }
    setDetail((prev) => {
      if (!prev) return prev;
      const events = insertSessionEvent(prev.events, event);
      // Same array back means the event was already held — hand React the
      // previous object so it can bail out of the render.
      return events === prev.events ? prev : { session: prev.session, events };
    });
  }, []);

  useEffect(() => {
    // A different conversation is a different transcript. Drop what we hold
    // before loading it, or the previous session's tail folds into the next
    // one's snapshot — the wholesale assign used to make this self-healing.
    setDetail(null);
    generationRef.current += 1;
    loadsInFlightRef.current = 0;
    fullLoadsInFlightRef.current = 0;
    loadedRef.current = false;
    rereadAfterLoadRef.current = false;
    patchedDuringLoadRef.current = null;
    readHighWaterRef.current = -1;
    backfillAfterLoadRef.current = false;
    pendingEventsRef.current.clear();
    const left = new AbortController();
    leftRef.current = left;
    void refresh();
    return () => {
      // Leaving the conversation: its reads land nowhere, so stop them and
      // stop retrying them.
      generationRef.current += 1;
      left.abort();
    };
  }, [refresh]);

  return {
    detail,
    loading,
    error,
    refresh,
    backfill,
    appendEvent,
    patchSession,
  };
}

interface MessageEnqueuedResponse {
  // null when the message interrupted a running turn: it's queued, and the
  // running turn persists + pushes the user_message over the WebSocket once
  // it flushes its reply, so it arrives there (after that reply) instead.
  event: ChatEventDto | null;
  session_status: SessionStatus;
}

export interface OutgoingImage {
  name: string;
  media_type: string;
  data_b64: string;
}

export async function sendMessage(
  agentId: string,
  sessionId: string,
  content: string,
  /** The id minted when the message was composed or queued (#13890), sent
   * with every copy of it so the server runs it once. Required: a send
   * without it can run twice. */
  clientMessageId: string,
  images: OutgoingImage[] = [],
  // The composer's effort level (#12389); null = no choice (the turn runs at Medium).
  thinkingEffort: ThinkingEffort | null = null,
  // The agent's own skill the message's leading /token names (#12080).
  skillId: string | null = null,
  // The RMS records the message @-mentions (#12154).
  referencedEntities: ReferencedEntity[] = [],
  /** What the run page's task panel showed when the message was sent
   * from it (#11942 round 13); absent from the whole-run view. */
  viewing?: string,
  /** The documents the message attaches (#12280), in chat's request shape. */
  attachments: AttachmentRequestFields = { attached_blob_ids: [] }
): Promise<MessageEnqueuedResponse> {
  const response = await jsonFetch<MessageEnqueuedResponse>(
    apiPath(`/agents/${agentId}/sessions/${sessionId}/messages`),
    {
      method: "POST",
      // The composer's effort level rides each turn (#12389), omitted when
      // the user chose none (Medium). No model: the organization's pin decides that
      // server-side.
      body: JSON.stringify({
        content,
        client_message_id: clientMessageId,
        images,
        ...(thinkingEffort ? { thinking_effort: thinkingEffort } : {}),
        ...attachments,
        ...(viewing !== undefined ? { viewing } : {}),
        ...(skillId ? { skill_id: skillId } : {}),
        ...(referencedEntities.length > 0
          ? {
              referenced_entities: referencedEntities.map((entity) => ({
                kind: entity.kind,
                id: entity.id,
              })),
            }
          : {}),
      }),
    }
  );
  return {
    event: response.event ?? null,
    session_status: response.session_status,
  };
}

/** Where a message's attached image is served from — the agents session
 *  route, which authorizes as a read of the session. Chat's transcript
 *  components fetch the bytes themselves given this (#11987). */
export function messageAttachmentUrl(
  agentId: string,
  sessionId: string,
  attachmentId: string
): string {
  return apiPath(
    `/agents/${agentId}/sessions/${sessionId}/attachments/${attachmentId}`
  );
}

export async function answerClarification(
  agentId: string,
  sessionId: string,
  eventId: string,
  answer: string
): Promise<MessageEnqueuedResponse> {
  const response = await jsonFetch<MessageEnqueuedResponse>(
    apiPath(`/agents/${agentId}/sessions/${sessionId}/answer-clarification`),
    {
      method: "POST",
      body: JSON.stringify({ event_id: eventId, answer }),
    }
  );
  return {
    event: response.event ?? null,
    session_status: response.session_status,
  };
}

/** Stop a running turn. The backend can't truly interrupt the remote
 *  runtime, but it frees the session (status back to idle) and pushes a
 *  session_complete frame so the chat unblocks immediately. */
export async function cancelSession(
  agentId: string,
  sessionId: string
): Promise<{ session_status: SessionStatus }> {
  return jsonFetch<{ session_status: SessionStatus }>(
    apiPath(`/agents/${agentId}/sessions/${sessionId}/cancel`),
    { method: "POST" }
  );
}

/** Re-read whenever the user looks at the page again — window focus, or a
 * background tab coming to the front.
 *
 * An agent's files are not only written by the person reading them: the agent
 * writes its own memory during a run, and a chat session can rewrite a bound
 * skill through the `save_skill` tool. A view that read once on mount therefore
 * shows a stale tree for as long as it sits open. This is a pull on reveal
 * rather than a server push, which is enough because the moment anyone can read
 * the view is the moment it is focused. Mirrors the visibility half of
 * ``useAgentCreditUsage``, without its poll. */
export function useRefreshOnReveal(refresh: () => void | Promise<void>): void {
  const lastRef = useRef(0);
  useEffect(() => {
    const readIfVisible = () => {
      if (document.visibilityState === "hidden") return;
      // Returning to a background tab fires BOTH `visibilitychange` and
      // `focus`, so the two events are one reveal and must cost one read. The
      // pair arrives in the same tick; anything later than that is a genuine
      // second reveal.
      const now = Date.now();
      if (now - lastRef.current < REVEAL_COALESCE_MS) return;
      lastRef.current = now;
      void refresh();
    };
    window.addEventListener("focus", readIfVisible);
    document.addEventListener("visibilitychange", readIfVisible);
    return () => {
      window.removeEventListener("focus", readIfVisible);
      document.removeEventListener("visibilitychange", readIfVisible);
    };
  }, [refresh]);
}

/** How close together two reveal events count as the same reveal. Comfortably
 * covers the same-tick `visibilitychange` + `focus` pair without swallowing a
 * real second look. */
const REVEAL_COALESCE_MS = 400;

/** How often a surface showing locked agent files asks again whether the run
 * has finished (#11988). Only while locked: an unlocked surface asks on
 * demand (a file opening, the cursor entering it, a refused write) and never
 * on a timer. */
export const AGENT_FILES_LOCK_POLL_MS = 3000;

/** How long the first lock read may take before a surface stops waiting on
 * it and treats the files as editable. The write's own refusal still guards
 * the data; a stalled read must not leave an admin at "Loading…" with the
 * file already in hand. */
export const AGENT_FILES_LOCK_READ_DEADLINE_MS = 5000;

const UNLOCKED_STATUS: MemoryLockStatus = {
  locked: false,
  run_id: null,
  run_title: null,
  conversation_id: null,
};

/** One agent's lock, as every surface showing its files sees it.
 *
 * The Files tab, the run rail's Agent zone and the pane beside a transcript
 * can all be on screen for one agent at once. Each holding its own answer
 * meant three reads of one fact, three polls, and a + that could disagree
 * with the open file by a poll. One query key per agent is what keeps them
 * saying the same thing: react-query shares the answer and the in-flight
 * read between every observer of a key, and polls only while one is mounted.
 *
 * This used to be a hand-built module-level `Map` of entries, listeners and
 * interval handles, with a `reset` export for stories (#12187). The query
 * cache is the same store with an owner: it belongs to the `QueryClient`, so
 * it is dropped with it and nothing survives from one story to the next. */
const agentFilesLockKey = (agentId: string) =>
  ["agents", agentId, "files-lock"] as const;

export interface AgentFilesLock {
  /** The last answer, or null until the first one lands for this agent. */
  status: MemoryLockStatus | null;
  /** Ask again now. Shares an in-flight read unless `fresh`, which abandons
   * it and sends its own. */
  recheck: (fresh?: boolean) => Promise<void>;
  /** The server has just refused a write because the agent is running: take
   * its word for it at once, before the read that names the run lands, so
   * the surface goes read-only and the poll starts without waiting. */
  markLocked: () => void;
}

/** Whether the agent is running, and so whether its files may be edited
 * (#11988). A run writes every agent file back to the store when its turn
 * ends, so an edit made during one is overwritten; the write routes refuse
 * with 409 while a turn is in flight. This is how a surface knows BEFORE the
 * user starts typing: it reads on mount and on reveal, the caller re-reads on
 * the moments that matter (a file opening, the cursor entering the sheet, a
 * refused write), and while locked it polls so the read-only state lifts on
 * its own when the run finishes.
 *
 * A failed read never locks the surface: the first one failing counts as
 * unlocked, and a later one leaves the last answer standing. A read that has
 * not answered by `AGENT_FILES_LOCK_READ_DEADLINE_MS` is a failed read. */
export function useAgentFilesLock(agentId: string | undefined): AgentFilesLock {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: agentFilesLockKey(agentId ?? ""),
    // The deadline rides on the request. A read that hangs must not hold the
    // slot: every poll tick and every recheck would share the same pending
    // promise and no request would ever leave again, so a locked surface
    // stayed locked until remount.
    queryFn: async ({ signal }) => {
      const request = new AbortController();
      const abort = () => request.abort();
      signal.addEventListener("abort", abort, { once: true });
      const deadline = setProductTimeout(
        abort,
        AGENT_FILES_LOCK_READ_DEADLINE_MS
      );
      try {
        return await jsonFetch<MemoryLockStatus>(
          apiPath(`/agents/${agentId}/memory/lock`),
          { signal: request.signal }
        );
      } catch (error) {
        // react-query abandoning the read (an unmount, a `fresh` recheck) is
        // not an answer; let it cancel.
        if (signal.aborted) throw error;
        // A failed read never locks the surface: the last answer stands, and
        // with none, the files are editable -- the write's own refusal still
        // guards the data. Resolved here rather than read off `isError`,
        // because a refetch from an error with no data goes back to "pending",
        // which read as "not known yet" and unmounted the open editor on every
        // recheck.
        return (
          queryClient.getQueryData<MemoryLockStatus>(
            agentFilesLockKey(agentId ?? "")
          ) ?? UNLOCKED_STATUS
        );
      } finally {
        clearProductTimeout(deadline);
        signal.removeEventListener("abort", abort);
      }
    },
    enabled: !!agentId,
    // Only while locked: an unlocked surface asks on demand and never on a
    // timer.
    refetchInterval: (q) =>
      q.state.data?.locked ? AGENT_FILES_LOCK_POLL_MS : false,
    // Every mount asks, even with an answer in hand from another surface.
    staleTime: 0,
    // Reveal is `useRefreshOnReveal` below, which coalesces the same-tick
    // `visibilitychange` + `focus` pair; react-query's own would double it.
    refetchOnWindowFocus: false,
    retry: false,
  });

  const recheck = useCallback(
    async (fresh = false): Promise<void> => {
      if (!agentId) return;
      // `fresh`: after a refused write, an answer whose request left before
      // the write proves nothing, so an in-flight read is abandoned for a new
      // one rather than shared.
      await queryClient.refetchQueries(
        { queryKey: agentFilesLockKey(agentId), exact: true },
        { cancelRefetch: fresh }
      );
    },
    [queryClient, agentId]
  );
  useRefreshOnReveal(recheck);

  const markLocked = useCallback(() => {
    if (!agentId) return;
    queryClient.setQueryData<MemoryLockStatus>(
      agentFilesLockKey(agentId),
      (previous) =>
        previous?.locked
          ? previous
          : {
              locked: true,
              run_id: null,
              run_title: null,
              conversation_id: null,
            }
    );
  }, [queryClient, agentId]);

  return { status: agentId ? (query.data ?? null) : null, recheck, markLocked };
}

export function useAgentMemory(agentId: string | undefined) {
  const [files, setFiles] = useState<MemoryFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `loading` describes the first read FOR THIS AGENT. A re-read on reveal must
  // not flick the rail back to its loading state, which would blank a tree the
  // user is looking at — but a different agent has genuinely not loaded yet, so
  // the ref holds which agent it is describing rather than a bare boolean.
  const loadedForRef = useRef<string | null>(null);
  // Reveal fires a read while one may still be in flight, so responses can
  // resolve out of order. Only the newest read is allowed to land.
  const readSeqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!agentId) {
      setFiles([]);
      setLoading(false);
      // Nothing is loaded for any agent now, so the next one — even the same
      // one coming back — gets a real first read, not "no files" for a frame.
      loadedForRef.current = null;
      return;
    }
    if (loadedForRef.current !== agentId) setLoading(true);
    setError(null);
    const seq = ++readSeqRef.current;
    try {
      const data = await jsonFetch<{ items: MemoryFile[] }>(
        apiPath(`/agents/${agentId}/memory`)
      );
      if (seq !== readSeqRef.current) return;
      setFiles(data.items);
      loadedForRef.current = agentId;
    } catch (err: unknown) {
      if (seq !== readSeqRef.current) return;
      // Only a FIRST read reports its failure. A re-read fires when a laptop
      // wakes or a tab comes forward, which is exactly when a request is most
      // likely to fail, and the rail renders the error in place of the list —
      // so reporting it would blank a tree the user is reading while the files
      // sit intact in state. The same rule the skills section follows.
      if (loadedForRef.current !== agentId) {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      if (seq === readSeqRef.current) setLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);
  useRefreshOnReveal(refresh);

  return { files, loading, error, refresh };
}

/** Overwrite the contents of a ``.md`` (or other text) output file the
 *  agent wrote for a run. Mirrors ``writeMemoryFile`` but targets the
 *  run-scoped output filesystem. The stored value is plain text — the
 *  inline editor hands back markdown source, so outputs round-trip
 *  unchanged. */
export async function writeRunOutput(
  agentId: string,
  runId: string,
  filename: string,
  content: string
): Promise<{ filename: string; content: string }> {
  return jsonFetch<{ filename: string; content: string }>(
    apiPath(`/agents/${agentId}/runs/${runId}/output`),
    {
      method: "PUT",
      body: JSON.stringify({ filename, content }),
    }
  );
}

/** Permanently delete a run-scoped output file the agent wrote via
 *  ``write_output``. Run-scoped (not session-scoped) because outputs
 *  live on the shared run filesystem. ``filename`` is the stored name
 *  (``.b64`` suffix intact for binary outputs). */
export async function deleteRunOutput(
  agentId: string,
  runId: string,
  filename: string
): Promise<{ ok: boolean }> {
  return jsonFetch<{ ok: boolean }>(
    apiPath(
      `/agents/${agentId}/runs/${runId}/output?filename=${encodeURIComponent(
        filename
      )}`
    ),
    { method: "DELETE" }
  );
}

/** Take attached documents out of a run's inputs (#12790), in one request:
 *  out of the run's selection and every conversation's, with their staged
 *  copies deleted. The documents themselves stay in Docs. */
export async function removeRunInputs(
  agentId: string,
  runId: string,
  blobIds: string[]
): Promise<{ ok: boolean }> {
  return jsonFetch<{ ok: boolean }>(
    apiPath(`/agents/${agentId}/runs/${runId}/inputs/remove`),
    {
      method: "POST",
      body: JSON.stringify({ blob_ids: blobIds }),
    }
  );
}

/** Rename a run-scoped output file. Both ``from`` and ``to`` are stored
 *  filenames; the caller is responsible for preserving any ``.b64``
 *  suffix so binary outputs keep their server-side decode behavior. */
export async function renameRunOutput(
  agentId: string,
  runId: string,
  from: string,
  to: string
): Promise<{ ok: boolean }> {
  return jsonFetch<{ ok: boolean }>(
    apiPath(`/agents/${agentId}/runs/${runId}/output/rename`),
    {
      method: "POST",
      body: JSON.stringify({ from, to }),
    }
  );
}

/** The output's ARTIFACT id (#11548 r61): the row run wind-down
 *  registered, minted on the spot when it never did. Attaching an output
 *  to a record is assigning this artifact — the panel hands the id to
 *  the same AssignArtifactDialog the Artifacts library uses. */
export async function ensureOutputArtifact(
  agentId: string,
  runId: string,
  filename: string
): Promise<string> {
  const body = await jsonFetch<{ artifact_id: string }>(
    apiPath(`/agents/${agentId}/runs/${runId}/outputs/artifact`),
    { method: "POST", body: JSON.stringify({ filename }) }
  );
  return body.artifact_id;
}

/** Auth-aware fetch of a session output. `preview` asks the backend to render
 *  office files inline as PDF; the rest is the shared authed-file-fetch idiom. */
export async function fetchSessionOutput(
  downloadPath: string,
  preview = false
): Promise<{ blob: Blob; filename: string }> {
  const separator = downloadPath.includes("?") ? "&" : "?";
  return fetchApiFile(
    preview ? `${downloadPath}${separator}preview=1` : downloadPath
  );
}

/** Programmatic auth-aware download of a session output. */
export async function downloadSessionOutput(
  downloadPath: string,
  fallbackName: string
): Promise<void> {
  return downloadApiFile(downloadPath, fallbackName);
}

/** The one place the run-outputs archive URL is spelled (#11921). */
export const runOutputsArchivePath = (agentId: string, runId: string): string =>
  `/api/v2/agents/${agentId}/runs/${runId}/outputs/archive`;

/** Every output of the run as one zip, named after the run by the server. */
export async function downloadRunOutputs(
  agentId: string,
  runId: string
): Promise<void> {
  return downloadApiFile(runOutputsArchivePath(agentId, runId), "outputs.zip");
}

/** The one place the agent-file download URL is spelled (#11921). */
const memoryDownloadPath = (
  agentId: string,
  path: string,
  preview: boolean
): string =>
  `/api/v2/agents/${agentId}/memory/download?path=${encodeURIComponent(
    path
  )}${preview ? "&preview=1" : ""}`;

/** An agent file's inline preview, with auth (#11921): the rendering an
 *  output gets — Office files come back as PDF, everything else as itself —
 *  with the viewer kind on the content type. */
export async function fetchMemoryPreview(
  agentId: string,
  path: string
): Promise<{ blob: Blob; filename: string }> {
  return fetchApiFile(memoryDownloadPath(agentId, path, true));
}

/** Programmatic auth-aware download of an agent file. */
export async function downloadMemoryFile(
  agentId: string,
  path: string
): Promise<void> {
  return downloadApiFile(
    memoryDownloadPath(agentId, path, false),
    path.split("/").pop() ?? path
  );
}

export type MemoryFileRead =
  | { path: string; is_binary: false; content: string }
  | { path: string; is_binary: true; content: null };

export async function readMemoryFile(
  agentId: string,
  path: string
): Promise<MemoryFileRead> {
  return jsonFetch<MemoryFileRead>(
    apiPath(`/agents/${agentId}/memory/file?path=${encodeURIComponent(path)}`)
  );
}

/** The agent's root brief and whether it is still the starter it was seeded
 *  with (#14877): an uploaded brief replaces a starter without asking. */
export interface MemoryBrief {
  content: string;
  is_starter: boolean;
}

export async function readMemoryBrief(agentId: string): Promise<MemoryBrief> {
  return jsonFetch<MemoryBrief>(apiPath(`/agents/${agentId}/memory/brief`));
}

export async function writeMemoryFile(
  agentId: string,
  path: string,
  content: string
): Promise<{ path: string; content: string }> {
  return jsonFetch<{ path: string; content: string }>(
    apiPath(`/agents/${agentId}/memory/file?path=${encodeURIComponent(path)}`),
    {
      method: "PUT",
      body: JSON.stringify({ content }),
    }
  );
}

/** Upload a single file into memory. Folder uploads are done by the
 *  caller as N sequential calls, one per file, using each file's
 *  ``webkitRelativePath`` as ``path`` so structure is preserved. */
export async function uploadMemoryFile(
  agentId: string,
  path: string,
  file: File | Blob,
  filename?: string,
  ingressBatch: string | null = null
): Promise<MemoryFile> {
  const form = new FormData();
  form.append("path", path);
  if (ingressBatch) form.append("ingress_batch", ingressBatch);
  form.append(
    "file",
    file,
    filename ?? (file instanceof File ? file.name : "upload.bin")
  );
  const response = await fpFetch(apiPath(`/agents/${agentId}/memory/upload`), {
    method: "POST",
    body: form,
  });
  if (!response.ok) {
    throw await errorFromResponse(response);
  }
  return (await response.json()) as MemoryFile;
}

// Files an operating system writes beside a user's own — Finder's folder
// metadata, AppleDouble resource forks, Explorer's thumbnail cache — that
// ride along in every folder pick and mean nothing to the agent (#12129).
// Kept in step with ``is_os_metadata_path`` on the backend, which refuses
// them for any client that sends one anyway.
const OS_METADATA_NAMES = new Set([".ds_store", "thumbs.db", "desktop.ini"]);
const OS_METADATA_DIRS = new Set(["__macosx"]);

export const isOsMetadataPath = (path: string): boolean => {
  const parts = path.split("/").filter(Boolean);
  if (parts.length === 0) return false;
  const name = parts[parts.length - 1].toLowerCase();
  if (OS_METADATA_NAMES.has(name) || name.startsWith("._")) return true;
  return parts
    .slice(0, -1)
    .some((part) => OS_METADATA_DIRS.has(part.toLowerCase()));
};

/** A file inside a dot-prefixed folder under ``skills/``, such as a
 *  ``.claude`` or ``.git`` folder that rode in with an imported project
 *  (#12446). Skills never read a hidden entry, and a ``.claude`` folder there
 *  was out of the agent's reach. A hidden FILE (``.gitignore``) is the user's
 *  own and is kept (#12129). Kept in step with
 *  ``is_hidden_skill_folder_path`` on the backend, which refuses them. */
export const isHiddenSkillFolderPath = (path: string): boolean => {
  const parts = path.split("/").filter(Boolean);
  if (parts.length < 3 || parts[0] !== "skills") return false;
  return parts.slice(1, -1).some((part) => part.startsWith("."));
};

/** What an upload leaves behind without a request. */
const isNeverSent = (path: string): boolean =>
  isOsMetadataPath(path) || isHiddenSkillFolderPath(path);

export interface MemoryUploadFailure {
  path: string;
  /** The server's reason, ready to show. */
  message: string;
}

/** What became of a multi-file upload into an agent's files. */
export interface MemoryUploadOutcome {
  /** Paths that landed, in the order they were sent. */
  landed: string[];
  /** Paths the server rejected, each with the reason. */
  failed: MemoryUploadFailure[];
  /** OS metadata, and files in a skill's hidden folders, never sent. */
  skipped: string[];
}

/** Upload every picked file into memory, one request per file as before,
 *  and keep going past a rejection (#12129): a folder import that stopped
 *  at its first rejected file left the rest unsent and said nothing about
 *  which, so a TIFF skill folder landed six of its files. OS metadata and a
 *  skill's hidden folders (#12446) are skipped without a request. Never
 *  throws: what landed, what failed with which reason, and what was skipped
 *  come back together. */
export async function uploadMemoryFiles(
  agentId: string,
  uploads: readonly { path: string; file: File | Blob }[]
): Promise<MemoryUploadOutcome> {
  const landed: string[] = [];
  const failed: MemoryUploadFailure[] = [];
  const skipped: string[] = [];
  const sending = uploads.filter(({ path }) => !isNeverSent(path));
  for (const { path } of uploads) {
    if (isNeverSent(path)) skipped.push(path);
  }
  // One staff ingress check for the whole folder (#10718): a refusal the
  // staff member declined fails every file and sends nothing.
  const preflight = await preflightStaffIngress(
    "agent_file",
    sending.map(({ path, file }) => ({ name: path, file }))
  );
  if (!preflight.proceed) {
    for (const { path } of sending) {
      failed.push({ path, message: "Not added." });
    }
    return { landed, failed, skipped };
  }
  for (const { path, file } of sending) {
    try {
      await uploadMemoryFile(agentId, path, file, undefined, preflight.batch);
      landed.push(path);
    } catch (err: unknown) {
      failed.push({
        path,
        message: userMessageOf(err, "The upload did not finish."),
      });
    }
  }
  return { landed, failed, skipped };
}

const fileCount = (n: number): string => (n === 1 ? "1 file" : `${n} files`);

/** At most this many paths are named per reason; a systemic failure (the
 *  agent running, the server down) rejects every file for one reason, and a
 *  16-file folder must not print that sentence sixteen times in the rail. */
const NAMED_FAILURES_PER_REASON = 3;

/** A line under the agent files tree: an error when it names a file that
 *  failed (or the action failed outright), otherwise information about what
 *  an import did (#14877), which must not read as a failure. */
export interface MemoryNotice {
  text: string;
  tone: "error" | "info";
}

/** A failure said on the same line. */
export const errorNotice = (text: string): MemoryNotice => ({
  text,
  tone: "error",
});

/** The line to put under the tree after an upload that added less than it
 *  was asked to: the rejected files grouped by the server's reason, or, when
 *  nothing was sent because the folder held only OS metadata or a skill's
 *  hidden folders, a line saying so rather than the silence that reads as a
 *  no-op. Null when every sent file landed. */
export const memoryUploadNotice = (
  outcome: MemoryUploadOutcome
): string | null => {
  if (outcome.failed.length === 0) {
    if (outcome.landed.length === 0 && outcome.skipped.length > 0) {
      return `Nothing was added: the folder held only system and hidden files (${outcome.skipped.length} skipped).`;
    }
    return null;
  }
  const byReason = new Map<string, string[]>();
  for (const failure of outcome.failed) {
    const paths = byReason.get(failure.message) ?? [];
    paths.push(failure.path);
    byReason.set(failure.message, paths);
  }
  const groups = Array.from(byReason.entries()).map(([reason, paths]) => {
    const named = paths.slice(0, NAMED_FAILURES_PER_REASON).join(", ");
    const rest = paths.length - NAMED_FAILURES_PER_REASON;
    const list = rest > 0 ? `${named} and ${rest} more` : named;
    return `${list} (${reason})`;
  });
  return `${fileCount(outcome.failed.length)} could not be added: ${groups.join("; ")}`;
};

export async function deleteMemoryFile(
  agentId: string,
  path: string
): Promise<void> {
  await jsonFetch<{ deleted: boolean }>(
    apiPath(`/agents/${agentId}/memory/file?path=${encodeURIComponent(path)}`),
    { method: "DELETE" }
  );
}

/** The agent's index: the file it reads at the start of every conversation.
 *  It is required, so its name is fixed (#12006, Hunter) — no rename surface
 *  offers it and the move helper below refuses it. Only the root file; a
 *  copy inside a folder is an ordinary file. */
export const AGENT_INDEX_FILE = "AGENT.md";

export const canRenameMemoryFile = (path: string): boolean =>
  path !== AGENT_INDEX_FILE;

/** AGENT.md cannot be deleted on its own either (#12883): a listing that finds
 *  it missing re-seeds the starter text, so a delete would replace the brief
 *  silently. The server refuses it too; deleting the agent is the way out. */
export const canDeleteMemoryFile = (path: string): boolean =>
  path !== AGENT_INDEX_FILE;

/** Move a memory file to a new path. No dedicated server endpoint —
 *  transfer raw bytes, then delete the source only after upload succeeds. Idempotent
 *  on the destination if it exists; the new write overwrites. */
export async function moveMemoryFile(
  agentId: string,
  fromPath: string,
  toPath: string
): Promise<void> {
  if (fromPath === toPath) return;
  // The floor under every rename surface, including a drag into a folder.
  if (!canRenameMemoryFile(fromPath)) {
    throw new Error(`${AGENT_INDEX_FILE} can't be renamed or moved`);
  }
  const { blob, filename } = await fetchApiFile(
    memoryDownloadPath(agentId, fromPath, false)
  );
  await uploadMemoryFile(agentId, toPath, blob, filename);
  await deleteMemoryFile(agentId, fromPath);
}

// A document the agent pulled on its own. Also the shape the Inputs panel
// renders and opens EVERY input by (#12059): an attachment is a reference
// plus its Docs location.
export interface SessionReference {
  blob_id: string;
  filename: string | null;
  blob_kind: string | null;
}

export interface SessionInput extends SessionReference {
  // Folder components of the document's location in Docs. Null for
  // documents with no filesystem location. The Inputs panel groups inputs by
  // the folder they were picked through (`input_folders`), not by this, so
  // nothing in the frontend reads it today.
  filesystem_path: string[] | null;
}

/** A folder the run's inputs were picked through (#12495), shown as one
 * Inputs row: the pick's path (drive id first when the pick named a drive),
 * the name to show, and the inputs it holds. */
export interface SessionInputFolder {
  path: string[];
  name: string;
  blob_ids: string[];
}

export interface OutputAttachment {
  entity_kind: string;
  public_entity_id: string;
  entity_name: string;
}

export interface SessionOutput {
  filename: string;
  size_bytes: number;
  modified_at: string;
  download_url: string;
  // The RMS records this output is attached to (#11548 r56). Populated on
  // the run listing; a conversation's own listing leaves it empty.
  attachments?: OutputAttachment[];
}

export interface SessionFiles {
  inputs: SessionInput[];
  references: SessionReference[];
  outputs: SessionOutput[];
  // Absent from a server that predates #12495; empty for a run with no
  // recorded pick.
  input_folders?: SessionInputFolder[];
}

export function useSessionFiles(
  agentId: string | undefined,
  sessionId: string | undefined
) {
  const [files, setFiles] = useState<SessionFiles>({
    inputs: [],
    references: [],
    outputs: [],
  });
  const [loading, setLoading] = useState(true);
  // The last read failed: the empty lists then stand for nothing, and a
  // reader that says "no outputs" must say it could not read them instead.
  const [failed, setFailed] = useState(false);

  const refresh = useCallback(async () => {
    if (!agentId || !sessionId) {
      setFiles({ inputs: [], references: [], outputs: [] });
      setFailed(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await jsonFetch<SessionFiles>(
        apiPath(`/agents/${agentId}/sessions/${sessionId}/files`)
      );
      setFiles(data);
      setFailed(false);
    } catch {
      setFiles({ inputs: [], references: [], outputs: [] });
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [agentId, sessionId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { files, loading, failed, refresh };
}

/** One of an agent's own skills — a `skills/<name>/SKILL.md` folder (or a
 *  legacy flat file) in its Agent Files, as the composer's "/" menu lists
 *  it (#12080). */
export interface AgentMemorySkill {
  name: string;
  description: string;
}

// The two file shapes that define a skill, as the server lists them: a
// folder's SKILL.md one level under `skills/`, or a flat markdown file there.
const SKILL_FILE_RE = /^skills\/(?:[^/]+\/SKILL\.md|[^/]+\.md)$/;

/** The agent's own skills, for the composer's "/" menu and the transcript's
 *  token highlight (#12080). A failed read leaves the list empty rather than
 *  surfacing: the composer still sends, only without the menu.
 *
 *  `memoryFiles` is the caller's Agent Files listing when it holds one: the
 *  skills are re-read whenever that listing's `skills/` entries change, so a
 *  skill the agent writes during a turn (the turn's end refetches the
 *  listing) or one saved from the Files tab reaches the menu without a
 *  reload. A caller with no listing reads once per agent. */
export function useAgentSkills(
  agentId: string | undefined,
  memoryFiles?: MemoryFile[]
): { skills: AgentMemorySkill[] } {
  // What the menu depends on, in listing terms: the path, size and mtime of
  // each file that can define a skill — a folder's `skills/<name>/SKILL.md`
  // or a legacy flat `skills/<name>.md`, the two shapes the server lists. A
  // skill's supporting files and anything written elsewhere change none of
  // them, so a listing refetch that moves nothing here costs no re-read.
  const skillFilesKey = useMemo(
    () =>
      memoryFiles === undefined
        ? null
        : JSON.stringify(
            memoryFiles
              .filter((f) => SKILL_FILE_RE.test(f.path))
              .map((f) => [f.path, f.size_bytes, f.modified_at])
              .sort()
          ),
    [memoryFiles]
  );
  // A query, keyed on the listing's skill files, so a changed listing is a
  // new read and only the newest one lands (an older read answering late
  // belongs to an older key). It is also what lets the page know the read is
  // under way from the moment the menu mounts: a hand-rolled fetch was
  // invisible until its request reached the mock layer, and a phone story
  // measured its page in that gap (#12551).
  const query = useQuery({
    queryKey: ["agents", agentId, "memory-skills", skillFilesKey],
    queryFn: async ({ signal }): Promise<AgentMemorySkill[]> => {
      try {
        const data = await jsonFetch<{ skills: AgentMemorySkill[] }>(
          apiPath(`/agents/${agentId}/memory/skills`),
          { signal }
        );
        return data.skills;
      } catch (err: unknown) {
        if (signal.aborted) throw err;
        console.error("Failed to load agent skills:", err);
        return [];
      }
    },
    enabled: Boolean(agentId),
    retry: false,
    refetchOnWindowFocus: false,
    // A re-read for the same agent keeps the menu it has until the new list
    // lands; another agent's list is never shown in the meantime.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] === agentId ? previous : undefined,
  });

  return { skills: query.data ?? NO_SKILLS };
}

const NO_SKILLS: AgentMemorySkill[] = [];
